import { useState, useEffect, useContext, useCallback } from "react";
import StarekContext from "../context/StarekContext";
import {
    fetchDatapoints,
    calculateProductionData,
} from "../utils/productionCalculations";

/**
 * Custom hook for fetching and calculating production data
 *
 * @param {Array} devices - Shadow devices with job schedules
 * @param {Object} timeRange - {start: Date|string, end: Date|string}
 * @param {Object} cachedDatapointsData - Pre-fetched datapoints data (from parent cache)
 * @param {Boolean} isLoadingDatapoints - Loading state of cached datapoints
 * @returns {Object} {data, loading, error, refetch}
 */
export function useProductionData(
    devices,
    timeRange,
    cachedDatapointsData = null,
    isLoadingDatapoints = false,
) {
    const [data, setData] = useState(null);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);
    const starekContext = useContext(StarekContext);

    const fetchData = useCallback(async () => {
        console.log("useProductionData - fetchData called");
        console.log("useProductionData - devices:", devices?.length || 0);
        console.log("useProductionData - timeRange:", timeRange);
        console.log(
            "useProductionData - cachedDatapointsData provided:",
            !!cachedDatapointsData,
        );

        if (!devices || devices.length === 0) {
            console.log(
                "useProductionData - NO DEVICES, returning empty array",
            );
            setData([]);
            return;
        }

        if (!timeRange?.start || !timeRange?.end) {
            console.log(
                "useProductionData - NO TIME RANGE, skipping datapoint fetch",
            );
            // No time range specified, skip datapoint fetch
            setData(devices);
            return;
        }

        console.log("useProductionData - proceeding to process data");

        const starekContextLocal = starekContext;

        try {
            let datapointsResponse;

            // Use cached datapoints if provided, otherwise fetch fresh
            if (cachedDatapointsData) {
                console.log("useProductionData - using cached datapoints");
                datapointsResponse = cachedDatapointsData;
                setLoading(isLoadingDatapoints);
            } else {
                console.log("useProductionData - fetching fresh datapoints");
                setLoading(true);

                const workspace = starekContextLocal.lastUsedWorkspace;
                const authToken = starekContextLocal.auth;

                console.log(
                    "useProductionData - workspace:",
                    workspace?.name || "MISSING",
                );
                console.log(
                    "useProductionData - authToken:",
                    authToken ? "EXISTS" : "MISSING",
                );

                if (!workspace || !authToken) {
                    console.error(
                        "useProductionData - MISSING WORKSPACE OR AUTH TOKEN, aborting",
                    );
                    setError(new Error("Missing workspace or auth token"));
                    return;
                }

                const deviceIds = devices.map((d) => d.id);
                const startTime = Math.floor(
                    new Date(timeRange.start).getTime() / 1000,
                );
                const endTime = Math.floor(
                    new Date(timeRange.end).getTime() / 1000,
                );

                console.log("useProductionData - fetching datapoints with:", {
                    deviceIds,
                    startTime,
                    endTime,
                    workspaceName: workspace?.name,
                });

                datapointsResponse = await fetchDatapoints({
                    deviceIds,
                    startTime,
                    endTime,
                    workspace,
                    authToken,
                });
            }

            console.log(
                "useProductionData - datapoints response:",
                datapointsResponse,
            );

            const calculatedData = calculateProductionData(
                devices,
                datapointsResponse,
                starekContextLocal.types,
            );
            console.log("useProductionData - calculatedData:", calculatedData);
            setData(calculatedData);
            setError(null);
        } catch (err) {
            console.error("Error fetching production data:", err);
            setError(err);
            // Fallback to devices without production data
            setData(devices);
        } finally {
            if (!cachedDatapointsData) {
                setLoading(false);
            }
        }
    }, [
        devices,
        timeRange,
        cachedDatapointsData,
        isLoadingDatapoints,
        starekContext,
    ]);

    useEffect(() => {
        fetchData();
    }, [fetchData]);

    return {
        data,
        loading,
        error,
        refetch: fetchData,
    };
}
